// src/systems/deck.ts
//
// Phase 4 — generic deck mechanics. Pure module; depends only on rng.
//
// Deck<T> is a draw/discard pair. Top of draw is the LAST element of the array
// (so drawOne is O(1) via pop). When draw empties, discard is shuffled into draw.

import type { Rng } from './rng';
import { shuffle } from './rng';

export interface Deck<T> {
  draw: T[]; // remaining cards (top of stack at end of array — pop = O(1))
  discard: T[]; // discarded cards
}

export function createDeck<T>(items: readonly T[], rng: Rng): Deck<T> {
  return { draw: shuffle(rng, items), discard: [] };
}

export function drawOne<T>(deck: Deck<T>, rng: Rng): T | null {
  if (deck.draw.length === 0) {
    if (deck.discard.length === 0) return null;
    deck.draw = shuffle(rng, deck.discard);
    deck.discard = [];
  }
  return deck.draw.pop() ?? null;
}

export function drawN<T>(deck: Deck<T>, n: number, rng: Rng): T[] {
  const out: T[] = [];
  for (let i = 0; i < n; i++) {
    const card = drawOne(deck, rng);
    if (card === null) break;
    out.push(card);
  }
  return out;
}

export function discard<T>(deck: Deck<T>, items: T[]): void {
  deck.discard.push(...items);
}

// Reshuffle ALL cards (draw + discard) into draw — used at round boundaries for ball deck per spec.
export function reshuffleAll<T>(deck: Deck<T>, rng: Rng): void {
  deck.draw = shuffle(rng, [...deck.draw, ...deck.discard]);
  deck.discard = [];
}
